import { useState } from 'react';
import { ExternalLink, Loader2, Play, ChevronDown, ChevronRight } from 'lucide-react';

function Section({ title, badge, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
        )}
        <h4 className="flex-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">{title}</h4>
        {badge}
      </button>
      {open && <div className="p-3 text-xs text-slate-700">{children}</div>}
    </section>
  );
}

export function resolveSourceBadgeLabel(sourceBadge) {
  switch (sourceBadge) {
    case 'sql':
      return { text: 'On tenant', className: 'bg-emerald-100 text-emerald-800' };
    case 'rest':
      return { text: 'REST rules', className: 'bg-violet-100 text-violet-800' };
    case 'tenant':
      return { text: 'On tenant', className: 'bg-emerald-100 text-emerald-800' };
    case 'fusion':
      return { text: 'Fusion (open)', className: 'bg-cyan-100 text-cyan-900' };
    default:
      return { text: 'Seeded', className: 'bg-slate-200 text-slate-600' };
  }
}

export default function BpmTaskConfigOverview({
  task,
  configuration,
  configLoading,
  onLoadRuntime,
  runtimeLoading,
  runtimeSamples,
}) {
  if (!task) return null;

  const displayMode = configuration?.displayMode || 'sql_fusion_reference';
  const tenantSql = configuration?.tenantSql || configuration?.sqlMatchRow;
  const fusionLinks = configuration?.fusionLinks || configuration?.fusionUrls;
  const attributes = configuration?.sqlAttributes || tenantSql?.attributes || [];

  if (configLoading && !configuration) {
    return (
      <p className="flex items-center gap-2 p-4 text-xs text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading tenant configuration…
      </p>
    );
  }

  return (
    <div className="space-y-3 p-4">
      {configuration?.summary && (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800">
          {configuration.summary}
        </p>
      )}

      <Section
        title="Tenant (SQL)"
        badge={
          tenantSql ? (
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800">
              matched
            </span>
          ) : (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">no row</span>
          )
        }
      >
        {tenantSql ? (
          <dl className="grid gap-2 sm:grid-cols-2">
            <div>
              <dt className="text-[10px] font-semibold uppercase text-slate-500">Composite</dt>
              <dd className="font-medium text-slate-900">{tenantSql.compositeName || tenantSql.name}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase text-slate-500">Task definition</dt>
              <dd className="font-mono text-[11px]">{tenantSql.taskDefinitionName || '—'}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase text-slate-500">Status</dt>
              <dd>{tenantSql.status || '—'}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase text-slate-500">Definition</dt>
              <dd>{tenantSql.definition || tenantSql.description || '—'}</dd>
            </div>
            {tenantSql.bypassOutcome && (
              <div className="sm:col-span-2">
                <dt className="text-[10px] font-semibold uppercase text-slate-500">Bypass outcome</dt>
                <dd>{tenantSql.bypassOutcome}</dd>
              </div>
            )}
          </dl>
        ) : (
          <p className="text-slate-500">No matching configuration found for this task.</p>
        )}
        {attributes.length > 0 && (
          <div className="mt-3">
            <p className="mb-1 text-[10px] font-semibold uppercase text-slate-500">
              Attributes
            </p>
            <ul className="max-h-32 overflow-y-auto divide-y divide-slate-100 rounded border border-slate-100">
              {attributes.slice(0, 12).map((a, i) => (
                <li key={i} className="flex justify-between gap-2 px-2 py-1 font-mono text-[10px]">
                  <span className="text-slate-700">{a.name}</span>
                  <span className="text-slate-500 truncate">{a.value || a.type}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      <Section
        title="Fusion Transaction Console"
        badge={
          fusionLinks?.taskId ? (
            <span className="font-mono text-[9px] text-slate-500">{fusionLinks.taskId}</span>
          ) : null
        }
      >
        <p className="mb-2 text-[11px] text-slate-600">
          {fusionLinks?.navigatorPath || 'Navigator → Tools → Transaction Console'}
        </p>
        <div className="flex flex-wrap gap-2">
          {(fusionLinks?.links || []).map((link) => (
            <a
              key={link.id}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-800"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {link.label}
            </a>
          ))}
        </div>
      </Section>

      {displayMode !== 'rest_rules' && (
        <Section title="Runtime sample (optional)" defaultOpen={false}>
          <p className="mb-2 text-[11px] text-slate-500">
            Load recent BPM worklist tasks only after you submit a test transaction — not required to view
            configuration.
          </p>
          <button
            type="button"
            onClick={onLoadRuntime}
            disabled={runtimeLoading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {runtimeLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            Load runtime sample
          </button>
          {(runtimeSamples || []).length > 0 && (
            <ul className="mt-2 divide-y divide-slate-100">
              {runtimeSamples.slice(0, 5).map((t, i) => (
                <li key={i} className="py-1.5">
                  {t.taskDefinitionName || t.title} · {t.state}
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}
    </div>
  );
}
